// A drawable slice of the investigation graph: anchors grouped by kind
// into columns, the relations among the shown anchors, and the claims.
// Bounded per kind so a long session's hundreds of processes do not
// swamp the picture; the anchors a call just added come first, then the
// best-connected ones.
export const VIEW_KIND_ORDER = Object.freeze(["endpoint", "person", "session", "process", "message", "prompt", "host", "window"]);
export const EVIDENCE_MARK = Object.freeze({ execution: "E", conversation: "C", "prompt-summary": "P", directory: "D", derived: "d" });
export const DEFAULT_PER_KIND = 10;
export const DEFAULT_MAX_EDGES = 120;

export function marksOf(node) {
    return [...(node?.evidence || [])].map((e) => EVIDENCE_MARK[e] || "?").sort().join("");
}

export function pluralKind(kind, n) {
    if (n === 1) return kind;
    if (kind === "person") return "people";
    return kind.endsWith("s") ? `${kind}es` : `${kind}s`;
}

export function kindOfKey(key) {
    const i = String(key || "").indexOf(":");
    return i > 0 ? String(key).slice(0, i) : null;
}

export function countKinds(keys) {
    const out = {};
    for (const k of keys || []) {
        const kind = kindOfKey(k);
        if (kind) out[kind] = (out[kind] || 0) + 1;
    }
    return out;
}

export function graphView(graph, { highlight = [], perKind = DEFAULT_PER_KIND, maxEdges = DEFAULT_MAX_EDGES } = {}) {
    const hot = new Set(highlight);
    const degree = new Map();
    for (const e of graph.edges.values()) {
        degree.set(e.from, (degree.get(e.from) || 0) + 1);
        degree.set(e.to, (degree.get(e.to) || 0) + 1);
    }
    const nodes = [];
    const index = new Map();
    const columns = [];
    for (const kind of VIEW_KIND_ORDER) {
        const all = [...graph.nodes.values()].filter((n) => n.kind === kind);
        if (!all.length) continue;
        const ranked = all
            .map((n) => ({ n, isNew: hot.has(n.key), deg: degree.get(n.key) || 0 }))
            .sort((a, b) => (Number(b.isNew) - Number(a.isNew)) || (b.deg - a.deg) || String(a.n.label || a.n.id).localeCompare(String(b.n.label || b.n.id)));
        const picked = ranked.slice(0, perKind);
        const col = { kind, total: all.length, omitted: all.length - picked.length, nodes: [] };
        for (const { n, isNew, deg } of picked) {
            const i = nodes.length;
            index.set(n.key, i);
            nodes.push({ key: n.key, kind, label: String(n.label || "").slice(0, 60), marks: marksOf(n), isNew, degree: deg, column: columns.length });
            col.nodes.push(i);
        }
        columns.push(col);
    }
    const candidates = [...graph.edges.values()]
        .filter((e) => index.has(e.from) && index.has(e.to) && e.from !== e.to)
        .sort((a, b) => Number(hot.has(b.from) || hot.has(b.to)) - Number(hot.has(a.from) || hot.has(a.to)));
    const edges = [];
    const seen = new Set();
    for (const e of candidates) {
        const a = index.get(e.from);
        const b = index.get(e.to);
        const k = a < b ? `${a}-${b}` : `${b}-${a}`;
        if (seen.has(k)) continue;
        seen.add(k);
        edges.push([a, b, e.kind]);
        if (edges.length >= maxEdges) break;
    }
    // Statements are one sentence each and few; they are kept whole so the
    // block never shows a cut sentence.
    const claims = graph.claims.map((c) => ({ id: c.id, kind: c.kind, status: c.status, bearing: c.bearing === "context" ? "context" : "answers", statement: String(c.statement || "").slice(0, 2000) }));
    return { nodes, columns, edges, claims, totalNodes: graph.nodes.size, totalEdges: graph.edges.size };
}

// The drawable slice as the `relations` kernel primitive takes it, so the
// chat shows the graph with the same chrome as every other rendered block.
export function relationsSpec(view, { title = null, maxPerGroup = DEFAULT_PER_KIND } = {}) {
    const groupTotals = {};
    for (const c of view?.columns || []) groupTotals[c.kind] = c.total;
    return { kind: "relations", title, groups: (view?.columns || []).map((c) => c.kind), groupTotals, maxPerGroup, caption: "Filled dots were added by this call. Marks on hover: E execution, C conversation, P prompt summary, D directory." };
}

export function relationsData(view) {
    // An anchor without a label shows its bare id, not the kind-prefixed key.
    const nodes = (view?.nodes || []).map((n) => ({ id: n.key, group: n.kind, label: n.label || n.key.slice(n.key.indexOf(":") + 1), note: n.marks || "", highlight: Boolean(n.isNew) }));
    const links = (view?.edges || []).map(([a, b, kind]) => ({ source: view.nodes[a].key, target: view.nodes[b].key, kind }));
    return { nodes, links };
}

export function claimRows(view) {
    return (view?.claims || []).map((c) => ({ statement: c.statement, kind: c.kind, status: c.status, bearing: c.bearing }));
}

// The rows a claims block shows: a claim that only describes the anchor
// (bearing "context") carries that beside its status, so a reader's
// off-question but well-cited claim is not read as an answer.
export function claimDisplayRows(claims) {
    return (claims || []).map((c) => (c && c.bearing === "context" ? { ...c, status: `${c.status} \u00b7 context` } : c));
}
